import { Button, Form, Spin } from 'antd';
import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import usePrevious from 'commons/hooks/usePrevious';
import { REQUEST_STATUSES } from 'commons/types/common';
import { MEETING_TYPES } from 'commons/types/meetingsTypes';
import ScheduleCGUStep from 'components/schedule/ScheduleCGUStep';
import ScheduleDateStep from 'components/schedule/ScheduleDateStep';
import {
  ScheduleType,
  defaultSchedule,
} from 'components/schedule/ScheduleType';
import { useAppDispatch, useAppSelector } from 'stores/hooks';
import { useMeetingList } from 'stores/meetings/meetingsHooks';
import {
  getDeleteMeetingRequestStatus,
  getNextMeeting,
  getUpdateMeetingRequestStatus,
  updateMeetingAsync,
} from 'stores/meetings/meetingsSlice';
import { useServices, useUser } from 'stores/user/userHooks';
import { serviceFromZip } from 'commons/libs/meeting_utils';
import DeleteScheduleModal from './DeleteScheduleModal';

const SCHEDULE_STEPS = {
  date_choice: 'date',
  cgu: 'cgu',
};
export default function MoveSchedule() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [user] = useUser();
  const [services] = useServices();
  // Needed to get the last meeting to user do
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [meetings] = useMeetingList();
  const nextMeeting = useAppSelector(getNextMeeting);
  const [form] = Form.useForm();
  const [step, setStep] = useState<string>(SCHEDULE_STEPS.date_choice);
  const updateMeetingRequestStatus = useAppSelector(
    getUpdateMeetingRequestStatus,
  );
  const updateDeleteMeetingRequestStatus = usePrevious(
    updateMeetingRequestStatus,
  );
  const deleteMeetingRequestStatus = useAppSelector(
    getDeleteMeetingRequestStatus,
  );
  const previousDeleteMeetingRequestStatus = usePrevious(
    deleteMeetingRequestStatus,
  );
  const defaultScheduledForm = defaultSchedule(
    nextMeeting?.type || MEETING_TYPES.RDV_1,
    serviceFromZip(services, user?.patient?.zip || ''),
    nextMeeting?.id,
    nextMeeting?.metadata.employee_id,
  );
  const onChangeStep = useCallback(
    (nextStep: string) => () => setStep(nextStep),
    [],
  );
  const onFinalize = useCallback(
    (schedule: ScheduleType) => {
      dispatch(
        updateMeetingAsync({
          ...defaultScheduledForm,
          ...schedule,
        }),
      );
    },
    [dispatch, defaultScheduledForm],
  );

  useEffect(() => form.resetFields(), [form]);
  useEffect(() => {
    if (updateDeleteMeetingRequestStatus === REQUEST_STATUSES.PENDING) {
      if (updateMeetingRequestStatus === REQUEST_STATUSES.SUCCESS) {
        navigate('/?state=updated');
      }
    }
  }, [
    navigate,
    updateMeetingRequestStatus,
    updateDeleteMeetingRequestStatus,
    user?.email,
    nextMeeting,
  ]);
  useEffect(() => {
    if (previousDeleteMeetingRequestStatus === REQUEST_STATUSES.PENDING) {
      if (deleteMeetingRequestStatus === REQUEST_STATUSES.SUCCESS) {
        navigate('/?state=deletedMeeting');
      }
      if (deleteMeetingRequestStatus === REQUEST_STATUSES.ERROR) {
        navigate('/?state=deletedMeetingFail');
      }
    }
  }, [
    navigate,
    deleteMeetingRequestStatus,
    previousDeleteMeetingRequestStatus,
  ]);

  if (!nextMeeting) {
    return <Spin />;
  }

  return (
    <Form
      form={form}
      name='reschedule-form'
      onFinish={onFinalize}
      initialValues={defaultScheduledForm}
      className='flex flex-col items-center'
    >
      <div
        className={step === SCHEDULE_STEPS.date_choice ? 'w-full' : 'hidden'}
      >
        <ScheduleDateStep
          onNext={onChangeStep(SCHEDULE_STEPS.cgu)}
          form={form}
          doctorID={defaultScheduledForm.meeting.doctorID}
          nextRdvType={defaultScheduledForm.type}
          patient={user?.patient}
        />
        <DeleteScheduleModal
          meetingID={nextMeeting.id}
          type={nextMeeting.type}
        />
      </div>
      <div className={step === SCHEDULE_STEPS.cgu ? 'w-full' : 'hidden'}>
        <Button
          onClick={onChangeStep(SCHEDULE_STEPS.date_choice)}
          type='text'
        >
          Retour
        </Button>
        <ScheduleCGUStep
          reschedule
          form={form}
          nextRdvType={defaultScheduledForm.type}
          actionText='Modifier mon rendez-vous'
        />
      </div>
    </Form>
  );
}
